import { PART_LABELS } from '../constants/fingering';
import type { FingeringPart, FingeringMode } from '../types/fingering';

/**
 * ファイル名命名規則の共通部品 (issue #91)。
 *
 * 完成 PDF / MP3 / omfz のファイル名を単一規則で生成する。 backend
 * `score-engine/src/fingering/file_naming.py` と同一規則・同一出力になるよう実装し、
 * 両言語のテストで出力一致を保証する (言語跨ぎのため物理的な単一モジュール化は不可)。
 *
 * 確定規則 (issue #91 round 1 裁定):
 *   | 生成物    | パート譜 (mode='part') | 総譜 (mode='score')              |
 *   | 完成 PDF  | <元>_運指済.pdf       | <元>_<1st|2nd|3rd|4th>_運指済.pdf |
 *   | MP3       | <元>_運指済.mp3       | <元>_<1st|2nd|3rd|4th>_運指済.mp3 |
 *   | omfz      | <元>.omfz             | <元>_<1st|2nd|3rd|4th>.omfz       |
 *
 * 「元ファイル名」は末尾 .pdf (大小無視) 除去 → 生成物サフィックス (`_運指済` / part label /
 * `unknown`) を巻き戻し → 7 種禁則文字を _ に置換 → 空なら untitled。 全体 120 字上限、
 * 超過時は元名を先頭 40 字に切り詰める (サフィックスは保持)。
 *
 * issue #152: 巻き戻しは choke point ({@link buildBaseName} / {@link resolveOriginalTitle} /
 * {@link toOriginalTitle}) の内側に置く。 呼出し側で個別に剥がす方式は漏れる経路が必ず出るため禁止。
 */

/** backend FORBIDDEN_FILENAME_CHARS / driveZipArchive.ts FORBIDDEN_FILENAME_RE と同じ 7 種禁則文字。 */
const FORBIDDEN_FILENAME_RE = /[\\/:*?"<>|]/g;

const SUFFIX_PROCESSED = '_運指済';
const BASE_TRUNCATE_LEN = 40;
const MAX_FILENAME_LEN = 120;

/** 7 種禁則文字 \ / : * ? " < > | を _ に置換する。 */
function sanitize(s: string): string {
  return s.replace(FORBIDDEN_FILENAME_RE, '_');
}

/** 末尾 .pdf (大小無視) を除去する。 拡張子が無ければそのまま返す。 */
function stripPdfExtension(name: string): string {
  return name.toLowerCase().endsWith('.pdf') ? name.slice(0, -4) : name;
}

/** part_num (1-4) → '1st'-'4th'。 range 外は 'Guitar{n}' 経由で解決し、 無ければ str(part_num)。 */
function partLabelFromNum(partNum: number): string {
  const key = `Guitar${partNum}` as FingeringPart;
  return PART_LABELS[key] ?? String(partNum);
}

/**
 * part 名 ('Guitar1'-'Guitar4') を buildResultFileName に渡す part 番号 (1-4) に変換する。
 * 未指定は 0、 想定外の値は NaN を返す (どちらも mode='part' では使われない)。 既定値が要る
 * 呼出し側は `partNumFromPart(part) || 1` のように自分でフォールバックすること。
 */
export function partNumFromPart(part: string | undefined): number {
  return Number((part ?? '').replace('Guitar', ''));
}

/** 生成済みファイル名で使われる正規の part label (総譜のみ付与)。 */
const VALID_PART_LABELS = new Set(Object.values(PART_LABELS));

/**
 * part を正規 label (1st-4th) に解決する。 解決できない値 (unknown / 空 / 想定外) は null。
 * - 'Guitar1'-'Guitar4' → PART_LABELS で 1st-4th へ変換
 * - 既に '1st'-'4th' → そのまま (二重変換しない)
 * - それ以外 (unknown / '' 等) → null (infix を付けない、 issue #152)
 */
function resolvePartLabel(part: string): string | null {
  const mapped = PART_LABELS[part as FingeringPart];
  if (mapped) return mapped;
  if (VALID_PART_LABELS.has(part)) return part;
  return null;
}

/**
 * 生成済み完成 PDF / omfz 名の末尾に付き得るサフィックストークン (issue #152)。
 * buildResultFileName の `運指済` / part label と、 供給層の 'unknown' フォールバックを含む。
 */
const STRIPPABLE_SUFFIX_TOKENS = new Set<string>([
  SUFFIX_PROCESSED.slice(1), // '運指済'
  'unknown',
  ...Object.values(PART_LABELS), // '1st'-'4th'
  ...Object.keys(PART_LABELS), // 'Guitar1'-'Guitar4' (防御的)
]);

/**
 * 末尾トークン (`_` 区切り) が `tokens` に含まれる限り反復除去する汎用ヘルパ。
 * 先頭トークンまで削って空文字になることは避ける (`'_運指済'` → `'_運指済'`)。
 * ファイル名巻き戻し ({@link stripSuffixTokens}) と表示名素化 ({@link deriveDisplayTitle}) が
 * 別トークン集合で共有する。
 */
function stripTrailingTokens(name: string, tokens: Set<string>): string {
  let stripped = name;
  for (;;) {
    const idx = stripped.lastIndexOf('_');
    if (idx <= 0) break; // 先頭 '_' or 区切りなし → 空タイトル回避で打ち切り
    if (!tokens.has(stripped.slice(idx + 1))) break;
    stripped = stripped.slice(0, idx);
  }
  return stripped;
}

/**
 * 末尾トークン (`_` 区切り) が {@link STRIPPABLE_SUFFIX_TOKENS} である限り反復除去する。
 * 先頭トークンまで削って空文字になることは避ける (`'_運指済'` → `'_運指済'`)。
 */
function stripSuffixTokens(name: string): string {
  return stripTrailingTokens(name, STRIPPABLE_SUFFIX_TOKENS);
}

/**
 * 入力ファイル名から「元ファイル名」を生成する。
 * 末尾 .pdf (大小無視) を除去 → 生成物サフィックス (`_運指済` / part label / `unknown`) を
 * 巻き戻し → 禁則文字を _ に置換 → 空なら untitled。
 *
 * 巻き戻しを内包するので本関数だけで冪等 (`buildBaseName(buildBaseName(x)) === buildBaseName(x)`)。
 * 完成 PDF 名が命名ソースへ逆流してもサフィックスが積み上がらない (issue #152)。
 * サニタイズは必ず巻き戻しの後 (backend `build_base_name` と同順序)。 先にサニタイズすると
 * 禁則文字由来の `_` がトークン境界と誤認され曲名の一部を削る。
 * 長さ制限はサフィックス確定後に行うため、 ここでは切り詰めない。
 */
export function buildBaseName(originalName: string): string {
  return sanitize(stripSuffixTokens(stripPdfExtension(originalName))) || 'untitled';
}

/**
 * `<base><infix><extWithDot>` を組み立て、 120 字超なら base を 40 字に切り詰める。
 * infix はサフィックス部 (`_運指済` や `_1st_運指済`、 omfz は '')。 切り詰めは base のみに
 * 適用しサフィックス・拡張子は保持。
 */
function assemble(base: string, infix: string, extWithDot: string): string {
  const full = `${base}${infix}${extWithDot}`;
  if (full.length > MAX_FILENAME_LEN) {
    return `${base.slice(0, BASE_TRUNCATE_LEN)}${infix}${extWithDot}`;
  }
  return full;
}

/**
 * 完成 PDF / MP3 のファイル名を生成する。
 * パート譜: `<base>_運指済.<ext>` / 総譜: `<base>_<1st-4th>_運指済.<ext>`
 *
 * @param base buildBaseName の出力 (拡張子除去・サニタイズ済み)。
 * @param partNum パート番号 (1-4)。 mode='score' のときのみ 1st-4th として使う。
 * @param mode 'part' | 'score'。
 * @param ext 'pdf' | 'mp3' (先頭ドットなし)。
 */
export function buildResultFileName(
  base: string,
  partNum: number,
  mode: FingeringMode,
  ext: 'pdf' | 'mp3',
): string {
  const infix = mode === 'score'
    ? `_${partLabelFromNum(partNum)}${SUFFIX_PROCESSED}`
    : SUFFIX_PROCESSED;
  return assemble(base, infix, `.${ext}`);
}

/**
 * omfz のファイル名を生成する (日時 stamp なし)。
 * パート譜: `<base>.omfz` / 総譜: `<base>_<1st-4th>.omfz`
 *
 * @param base buildBaseName の出力。
 * @param part 'Guitar1'-'Guitar4' または既に label 化された '1st'-'4th'。 mode='score' の
 *   ときのみ 1st-4th として使う。 'unknown' / 空文字など解決不能な値は infix を付けない
 *   (issue #152: unknown 混入防止)。
 * @param mode 'part' | 'score'。
 */
export function buildOmfzFileName(base: string, part: string, mode: FingeringMode): string {
  const label = mode === 'score' ? resolvePartLabel(part) : null;
  const infix = label ? `_${label}` : '';
  return assemble(base, infix, '.omfz');
}

/**
 * ファイル名 / 汚染済みタイトルから「元タイトル」を巻き戻す (issue #152)。
 *
 * 末尾 .pdf を除去したのち、 末尾トークン (`_` 区切り) が `運指済` / part label
 * (1st-4th, Guitar1-4) / `unknown` のいずれかである限り反復除去する。 先頭トークンまで削って
 * 空文字になることは避ける。 backend `derive_original_title`
 * (score-engine/src/fingering/file_naming.py) と同一入出力。
 *
 * 元タイトルを作る唯一の正規化関数で、 {@link toOriginalTitle} / {@link resolveOriginalTitle} /
 * {@link buildBaseName} は内部で必ずこれを通る。 呼出し側が個別に巻き戻しを足す必要はない。
 */
export function deriveTitleFromFileName(fileName: string): string {
  return stripSuffixTokens(stripPdfExtension(fileName));
}

/**
 * 履歴・表示・appProperties.title 用のモード表示サフィックストークン (issue #173)。
 * UI ラベル (総譜 / パート譜) + 略記 (パート)。 ファイル名生成の
 * {@link STRIPPABLE_SUFFIX_TOKENS} とは別集合で、 buildBaseName / backend には影響しない
 * (実ファイル名の命名規則は不変)。
 */
const MODE_SUFFIX_TOKENS = new Set<string>(['総譜', 'パート譜', 'パート']);

/**
 * 表示・title 用の「素の曲名」を返す (issue #173)。
 * #152 の生成物サフィックス (`運指済` / 1st-4th / unknown 等) を
 * {@link deriveTitleFromFileName} で巻き戻したうえで、 モード表示サフィックス
 * (`総譜` / `パート譜` / `パート`) を追加で末尾から剥がす。 末尾 `_` 区切りトークンの
 * 厳密一致のみ除去するので曲名内の類似語は誤除去しない。 先頭トークンまで削って空文字に
 * することも避ける。 内包する derive が冪等なため本関数も冪等。
 * ファイル名生成 ({@link buildBaseName}) はこれを通さないので保存名は不変。
 */
export function deriveDisplayTitle(nameOrTitle: string): string {
  return stripTrailingTokens(deriveTitleFromFileName(nameOrTitle), MODE_SUFFIX_TOKENS);
}

/**
 * アップロードされた PDF のファイル名から「元タイトル」を確定する (issue #152)。
 *
 * 実体は {@link deriveTitleFromFileName} (アップロード経路の入口としての別名)。 完成 PDF
 * (`曲名_1st_運指済.pdf`) を再アップロードしても元タイトルは `曲名` になりサフィックスが二重付与
 * されない。 サニタイズ (禁則文字置換 / untitled 化) は {@link buildBaseName} の責務なので
 * **ここでは行わない** (元タイトルは表示・manifest にも使う)。
 */
export function toOriginalTitle(uploadFileName: string): string {
  return deriveTitleFromFileName(uploadFileName);
}

/** {@link resolveOriginalTitle} の入力。 上から順に優先される。 いずれも derive で消毒される。 */
export interface OriginalTitleSource {
  /** 保存済みの元タイトル (命名の正)。 旧データは汚染され得る。 */
  originalTitle?: string;
  /** fingering.json の meta.title。 旧データは汚染され得る。 */
  metaTitle?: string;
  /** 完成 PDF 名。 旧データの最終手段。 */
  fileName?: string;
}

/**
 * 命名ソースとなる「元タイトル」を解決する (issue #152)。
 *
 * 解決順序: `originalTitle` → `metaTitle` → `fileName` → `''`。 どのソースから来ても
 * {@link deriveTitleFromFileName} を通す (**素通ししない**)。 供給層が完成 PDF 名を
 * originalTitle へ書き戻す経路があり、 素通しすると `_3rd_運指済` が再生成のたびに積み上がる。
 * 一律消毒することで汚染済み保存データも 1 回の再生成で正規名へ収束する (自己治癒)。
 */
export function resolveOriginalTitle(src: OriginalTitleSource): string {
  return deriveTitleFromFileName(src.originalTitle || src.metaTitle || src.fileName || '');
}
